import React from "react";
import { Link } from "react-router-dom";
import {
  FaEdit,
  FaEye,
  FaPaperPlane,
  FaReply,
  FaTrash,
} from "react-icons/fa";
import ClientDocumentsButton from "./ClientDocumentsButton";
import {
  formatInquiryDisplayDate,
  getInquiryFieldsFromBatchItem,
  getMissingInquiryBatchFields,
  isBatchItemFieldMissing,
  canSendBatchItemToProvider,
  isResendBatchItemToProvider,
} from "./inquiryApplicationUtils";
import { formatBatchDateTime, getItemStatusMeta } from "./inquiryBatchUtils";

const CELL = "px-3 py-2.5 align-middle";
const HEAD_CELL =
  "sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgba(226,232,240,1)]";

const ACTION_BTN =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-45";

function StatusBadge({ meta }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function DataCell({ value, missing, className = "", mono = false, truncate = false }) {
  const display = value || "";
  const showMissing = missing || !display;

  return (
    <td className={`${CELL} ${className}`}>
      <span
        className={`block text-[12px] ${
          truncate ? "max-w-[180px] truncate" : ""
        } ${mono ? "tabular-nums" : ""} ${
          showMissing
            ? "font-medium text-red-500"
            : "font-medium text-slate-800"
        }`}
        title={truncate && display ? display : undefined}
      >
        {showMissing ? "—" : display}
      </span>
    </td>
  );
}

function ProviderGroupHeader({
  providerName,
  studentCount,
  pendingCount,
  sendableCount,
  allGroupSelected,
  onSelectAll,
}) {
  return (
    <tr className="bg-slate-100/80">
      <td colSpan={11} className="border-y border-slate-200 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-[#007BFF]" aria-hidden="true" />
            <span className="text-[12px] font-semibold text-slate-800">
              {providerName || "Unknown provider"}
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">
              {studentCount} student{studentCount === 1 ? "" : "s"}
            </span>
            {sendableCount > 0 ? (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-200">
                {sendableCount} ready to send/resend
              </span>
            ) : pendingCount > 0 ? (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200">
                {pendingCount} incomplete
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onSelectAll}
            className="inline-flex h-7 shrink-0 items-center rounded-md border border-[#007BFF]/25 bg-white px-2.5 text-[11px] font-semibold text-[#007BFF] hover:bg-[#007BFF]/5"
            style={{ boxShadow: "none" }}
          >
            {allGroupSelected ? "Selected" : "Select all"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function RowActions({
  busy,
  canSend,
  isResend = false,
  canViewReply,
  onEdit,
  onSend,
  onReply,
  onViewReply,
  onDelete,
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={onEdit}
        title="Edit inquiry details"
        aria-label="Edit inquiry details"
        className={`${ACTION_BTN} border-[#007BFF]/20 bg-[#007BFF]/5 text-[#007BFF] hover:bg-[#007BFF]/10 focus:ring-[#007BFF]/25`}
        style={{ boxShadow: "none" }}
      >
        <FaEdit size={13} />
      </button>
      {canSend ? (
        <button
          type="button"
          disabled={busy}
          onClick={onSend}
          title={isResend ? "Resend to provider" : "Send to provider"}
          aria-label={isResend ? "Resend to provider" : "Send to provider"}
          className={`${ACTION_BTN} border-[#007BFF]/20 bg-[#007BFF]/10 text-[#007BFF] hover:bg-[#007BFF]/15 focus:ring-[#007BFF]/25`}
          style={{ boxShadow: "none" }}
        >
          <FaPaperPlane size={12} />
        </button>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={onReply}
        title="Record provider reply"
        aria-label="Record provider reply"
        className={`${ACTION_BTN} border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 focus:ring-violet-200`}
        style={{ boxShadow: "none" }}
      >
        <FaReply size={13} />
      </button>
      {canViewReply ? (
        <button
          type="button"
          disabled={busy}
          onClick={onViewReply}
          title="View reply"
          aria-label="View reply"
          className={`${ACTION_BTN} border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 focus:ring-sky-200`}
          style={{ boxShadow: "none" }}
        >
          <FaEye size={13} />
        </button>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        title="Remove from batch"
        aria-label="Remove from batch"
        className={`${ACTION_BTN} border-red-200 bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-200`}
        style={{ boxShadow: "none" }}
      >
        <FaTrash size={12} />
      </button>
    </div>
  );
}

function LoadingRows({ colSpan }) {
  return Array.from({ length: 5 }, (_, index) => (
    <tr key={index} className="animate-pulse">
      <td colSpan={colSpan} className={CELL}>
        <div
          className="h-9 rounded bg-slate-100"
          style={{ width: `${88 - index * 8}%` }}
        />
      </td>
    </tr>
  ));
}

function countProviderStudents(items, providerName, startIndex) {
  let count = 0;
  for (let i = startIndex; i < items.length; i += 1) {
    if (String(items[i]?.provider_name || "") !== String(providerName || "")) {
      break;
    }
    count += 1;
  }
  return count;
}

function InquiryBatchStudentsTable({
  batch,
  filteredItems,
  loading,
  busy,
  selectedIds,
  allSelectableChecked,
  selectableItems,
  providerSearch,
  onToggleAll,
  onToggleItem,
  onSelectProviderGroup,
  onEditItem,
  onOpenDocuments,
  onSendItem,
  onReplyItem,
  onViewReplyItem,
  onDeleteItem,
}) {
  const colSpan = 11;

  return (
    <div className="cms-commissions-table-wrap max-h-[min(70vh,720px)] overflow-auto">
      <table className="w-full min-w-[1080px] border-collapse text-left text-[13px]">
        <thead>
          <tr>
            <th className={`${HEAD_CELL} w-10`}>
              <input
                type="checkbox"
                checked={allSelectableChecked}
                onChange={onToggleAll}
                disabled={selectableItems.length === 0}
                aria-label="Select all students"
              />
            </th>
            <th className={`${HEAD_CELL} min-w-[150px]`}>Student</th>
            <th className={`${HEAD_CELL} whitespace-nowrap`}>Student ID</th>
            <th className={`${HEAD_CELL} whitespace-nowrap`}>DOB</th>
            <th className={`${HEAD_CELL} min-w-[160px]`}>Course</th>
            <th className={`${HEAD_CELL} min-w-[120px]`}>Intake</th>
            <th className={`${HEAD_CELL} whitespace-nowrap`}>Start</th>
            <th className={`${HEAD_CELL} whitespace-nowrap`}>End</th>
            <th className={`${HEAD_CELL} w-14 text-center`}>Docs</th>
            <th className={`${HEAD_CELL} whitespace-nowrap`}>Status</th>
            <th className={`${HEAD_CELL} w-[168px] text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {loading ? (
            <LoadingRows colSpan={colSpan} />
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
              const itemMeta = getItemStatusMeta(item.item_status);
              const fields = getInquiryFieldsFromBatchItem(item, batch);
              const missingFields = getMissingInquiryBatchFields(fields);
              const selectable = !["moved_to_commission", "cancelled"].includes(
                item.item_status,
              );
              const canSend = canSendBatchItemToProvider(item, batch);
              const canViewReply =
                Boolean(item.latest_reply) ||
                ["replied", "moved_to_commission"].includes(item.item_status);
              const prevProvider =
                index > 0 ? filteredItems[index - 1]?.provider_name : null;
              const showProviderHeader =
                String(item.provider_name || "") !== String(prevProvider || "");
              const isSelected = selectedIds.has(Number(item.id));
              const providerGroupItems = showProviderHeader
                ? countProviderStudents(filteredItems, item.provider_name, index)
                : 0;
              const providerGroupRows = showProviderHeader
                ? filteredItems.slice(index, index + providerGroupItems)
                : [];
              const providerSelectableIds = providerGroupRows
                .filter(
                  (row) =>
                    !["moved_to_commission", "cancelled"].includes(row.item_status),
                )
                .map((row) => Number(row.id));
              const providerPendingCount = providerGroupRows.filter(
                (row) => row.item_status === "pending",
              ).length;
              const providerSendableCount = providerGroupRows.filter((row) =>
                canSendBatchItemToProvider(row, batch),
              ).length;
              const allGroupSelected =
                providerSelectableIds.length > 0 &&
                providerSelectableIds.every((id) => selectedIds.has(id));

              return (
                <React.Fragment key={item.id}>
                  {showProviderHeader ? (
                    <ProviderGroupHeader
                      providerName={item.provider_name}
                      studentCount={providerGroupItems}
                      pendingCount={providerPendingCount}
                      sendableCount={providerSendableCount}
                      allGroupSelected={allGroupSelected}
                      onSelectAll={() =>
                        onSelectProviderGroup?.(
                          item.provider_name,
                          index,
                          allGroupSelected,
                        )
                      }
                    />
                  ) : null}
                  <tr
                    className={`transition-colors even:bg-slate-50/40 hover:bg-slate-50/90 ${
                      isSelected ? "bg-[#007BFF]/[0.06] ring-1 ring-inset ring-[#007BFF]/15" : ""
                    }`}
                  >
                    <td className={CELL}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!selectable}
                        onChange={() => onToggleItem(item.id)}
                        aria-label={`Select ${item.student_name || "student"}`}
                      />
                    </td>
                    <td className={CELL}>
                      <div className="min-w-0">
                        <p className="mb-0 text-[13px] font-semibold leading-snug text-slate-900">
                          {item.client_id ? (
                            <Link
                              to={`/clients/view-client-detail/${item.client_id}`}
                              className="text-[#007BFF] hover:underline"
                            >
                              {fields.clientName || "—"}
                            </Link>
                          ) : (
                            fields.clientName || "—"
                          )}
                        </p>
                        {missingFields.length > 0 ? (
                          <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200">
                            {missingFields.length} missing
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <DataCell
                      value={fields.studentId}
                      missing={isBatchItemFieldMissing("studentId", fields)}
                      mono
                    />
                    <DataCell
                      value={formatInquiryDisplayDate(fields.dob)}
                      missing={isBatchItemFieldMissing("dob", fields)}
                      mono
                    />
                    <DataCell
                      value={fields.course}
                      missing={isBatchItemFieldMissing("course", fields)}
                      truncate
                    />
                    <DataCell
                      value={fields.intake || item.intake}
                      missing={!String(fields.intake || item.intake || "").trim()}
                      truncate
                    />
                    <DataCell
                      value={formatInquiryDisplayDate(fields.start)}
                      missing={isBatchItemFieldMissing("start", fields)}
                      mono
                    />
                    <DataCell
                      value={formatInquiryDisplayDate(fields.end)}
                      missing={isBatchItemFieldMissing("end", fields)}
                      mono
                    />
                    <td className={`${CELL} text-center`}>
                      <ClientDocumentsButton
                        documents={item.documents}
                        clientName={fields.clientName || "Client"}
                        onOpen={onOpenDocuments}
                        className="mx-auto"
                      />
                    </td>
                    <td className={CELL}>
                      <div className="space-y-1">
                        <StatusBadge meta={itemMeta} />
                        {Number(item.provider_email_send_count) > 0 ? (
                          <p
                            className="mb-0 text-[10px] font-semibold text-slate-600"
                            title={
                              item.provider_email_send?.sent_at
                                ? `Last emailed ${item.provider_email_send.recipient_email || ""}`
                                : undefined
                            }
                          >
                            Sent {item.provider_email_send_count}x
                            {item.provider_email_send?.sent_at ? (
                              <span className="font-normal text-slate-500">
                                {" "}
                                · Last{" "}
                                {formatBatchDateTime(item.provider_email_send.sent_at)}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className={`${CELL} text-right`}>
                      <RowActions
                        busy={busy}
                        canSend={canSend}
                        isResend={isResendBatchItemToProvider(item)}
                        canViewReply={canViewReply}
                        onEdit={() => onEditItem(item)}
                        onSend={() => onSendItem(item)}
                        onReply={() => onReplyItem(item)}
                        onViewReply={() => onViewReplyItem(item)}
                        onDelete={() => onDeleteItem(item)}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={colSpan}
                className="px-3 py-12 text-center text-[13px] text-slate-500"
              >
                {providerSearch.trim()
                  ? "No students match this provider search."
                  : "No students in this batch."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default InquiryBatchStudentsTable;
